function qs(root, sel) {
  return /** @type {HTMLElement|null} */ (root.querySelector(sel));
}

async function changeCartLine({ line, sellingPlanId }) {
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      line,
      selling_plan: sellingPlanId,
    }),
  });
}

async function clearCart() {
  await fetch('/cart/clear.js', {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });
}

async function addSubscriptionItem({ variantId, sellingPlanId }) {
  await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
          selling_plan: sellingPlanId,
        },
      ],
    }),
  });
}

function getProductHandleFromItemUrl(itemUrl) {
  if (!itemUrl) return null;
  try {
    const path = itemUrl.split('?')[0] || '';
    const parts = path.split('/').filter(Boolean);
    const idx = parts.indexOf('products');
    if (idx === -1) return null;
    return parts[idx + 1] || null;
  } catch (_e) {
    return null;
  }
}

async function getSellingPlanIdForProductHandle(handle) {
  if (!handle) return null;
  const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
  if (!res.ok) return null;
  const product = await res.json();

  const groups = Array.isArray(product?.selling_plan_groups) ? product.selling_plan_groups : [];
  // Prefer a plan that looks like 12 weeks / 3 months, otherwise just pick first.
  for (const group of groups) {
    const plans = Array.isArray(group?.selling_plans) ? group.selling_plans : [];
    const match =
      plans.find((p) => typeof p?.name === 'string' && /12\s*week|3\s*month/i.test(p.name)) || plans[0] || null;
    if (match?.id) return match.id;
  }
  return null;
}

function openAuthDialog() {
  const dialog = /** @type {HTMLDialogElement|null} */ (document.getElementById('subscription-auth-dialog'));
  if (!dialog) return;
  if (!dialog.open) dialog.showModal();
}

function openConfirmDialog() {
  const dialog = /** @type {HTMLDialogElement|null} */ (document.getElementById('subscription-confirm-dialog'));
  if (!dialog) return;
  if (!dialog.open) dialog.showModal();
}

function closeConfirmDialog() {
  const dialog = /** @type {HTMLDialogElement|null} */ (document.getElementById('subscription-confirm-dialog'));
  if (!dialog) return;
  if (dialog.open) dialog.close();
}

function closeAuthDialog() {
  const dialog = /** @type {HTMLDialogElement|null} */ (document.getElementById('subscription-auth-dialog'));
  if (!dialog) return;
  if (dialog.open) dialog.close();
}

function setAuthDialogTab(tab) {
  const dialog = document.getElementById('subscription-auth-dialog');
  if (!dialog) return;

  const tabs = dialog.querySelectorAll('[data-subscription-auth-tab]');
  const panels = dialog.querySelectorAll('[data-subscription-auth-panel]');

  tabs.forEach((el) => el.classList.toggle('is-active', el.getAttribute('data-subscription-auth-tab') === tab));
  panels.forEach((el) => el.classList.toggle('hidden', el.getAttribute('data-subscription-auth-panel') !== tab));
}

function initAuthDialog() {
  const dialog = /** @type {HTMLDialogElement|null} */ (document.getElementById('subscription-auth-dialog'));
  if (!dialog) return;

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) closeAuthDialog();
  });

  dialog.querySelector('[data-subscription-auth-close]')?.addEventListener('click', closeAuthDialog);

  dialog.querySelectorAll('[data-subscription-auth-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = btn.getAttribute('data-subscription-auth-tab') || 'register';
      setAuthDialogTab(tab);
    });
  });

  dialog.querySelectorAll('[data-subscription-auth-switch]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = btn.getAttribute('data-subscription-auth-switch') || 'register';
      setAuthDialogTab(tab);
    });
  });
}

function initConfirmDialog() {
  const dialog = /** @type {HTMLDialogElement|null} */ (document.getElementById('subscription-confirm-dialog'));
  if (!dialog) return;

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) closeConfirmDialog();
  });

  dialog.querySelector('[data-subscription-confirm-close]')?.addEventListener('click', closeConfirmDialog);
  dialog.querySelector('[data-subscription-confirm-cancel]')?.addEventListener('click', closeConfirmDialog);
}

async function hydrateConfirmDialogFromCart({ frequencyText = 'Every 12 weeks' } = {}) {
  const dialog = document.getElementById('subscription-confirm-dialog');
  if (!dialog) return;

  const list = /** @type {HTMLElement|null} */ (dialog.querySelector('[data-subscription-confirm-items]'));
  const freq = /** @type {HTMLElement|null} */ (dialog.querySelector('[data-subscription-confirm-frequency]'));
  const discount = /** @type {HTMLElement|null} */ (dialog.querySelector('[data-subscription-confirm-discount]'));

  if (freq) freq.textContent = frequencyText;
  if (discount) discount.textContent = '—';
  if (list) list.replaceChildren();

  try {
    const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await cartRes.json();
    const items = Array.isArray(cart?.items) ? cart.items : [];

    for (const item of items) {
      const li = document.createElement('li');
      const title = item?.product_title || item?.title || 'Item';
      li.textContent = `${title} (every 12 weeks)`;
      list?.appendChild(li);
    }

    // Try to infer discount from first available plan name (e.g. "Deliver every 12 weeks, 10% off")
    const first = items[0];
    const handle = getProductHandleFromItemUrl(first?.url);
    if (handle) {
      const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      if (res.ok) {
        const product = await res.json();
        const groups = Array.isArray(product?.selling_plan_groups) ? product.selling_plan_groups : [];
        const plan = groups?.[0]?.selling_plans?.[0];
        const name = plan?.name;
        const m = typeof name === 'string' ? name.match(/(\d+%)/) : null;
        if (discount && m?.[1]) discount.textContent = `${m[1]} off`;
      }
    }
  } catch (_e) {
    // If cart fetch fails, still show dialog
  }
}

function initCartSubscribeUI() {
  document.addEventListener('click', async (e) => {
    const btn = /** @type {HTMLElement|null} */ ((/** @type {any} */ (e.target)).closest?.('[data-subscribe-checkout]'));
    if (!btn) return;

    const accountsEnabled = btn.getAttribute('data-customer-accounts-enabled') !== 'false';
    if (!accountsEnabled) {
      // Accounts are disabled in Shopify admin, so create/login cannot work.
      // Send them to regular checkout instead of erroring.
      window.location.href = '/checkout';
      return;
    }

    // First step: show confirmation window (customer can cancel)
    await hydrateConfirmDialogFromCart({ frequencyText: 'Every 12 weeks' });
    openConfirmDialog();

    const confirmBtn = document.querySelector('#subscription-confirm-dialog [data-subscription-confirm-continue]');
    if (!confirmBtn) return;

    // Avoid stacking listeners
    confirmBtn.replaceWith(confirmBtn.cloneNode(true));
    const freshConfirmBtn = /** @type {HTMLElement|null} */ (
      document.querySelector('#subscription-confirm-dialog [data-subscription-confirm-continue]')
    );
    if (!freshConfirmBtn) return;

    freshConfirmBtn.addEventListener(
      'click',
      async () => {
        closeConfirmDialog();

        const isAuthed = btn.getAttribute('data-customer-authenticated') === 'true';
        if (!isAuthed) {
          setAuthDialogTab('register');
          openAuthDialog();
          return;
        }

        // Use the selected product in the confirm dialog as the subscription item
        const productSelect = /** @type {HTMLSelectElement|null} */ (
          document.querySelector('#subscription-confirm-dialog [data-subscription-confirm-product]')
        );
        const variantId = Number(productSelect?.value || 0);
        const selectedOption = productSelect?.selectedOptions?.[0];
        const handle = selectedOption?.getAttribute('data-product-handle') || null;

        try {
          const sellingPlanId = handle ? await getSellingPlanIdForProductHandle(handle) : null;
          if (variantId && sellingPlanId) {
            await clearCart();
            await addSubscriptionItem({
              variantId,
              sellingPlanId,
            });
          }
        } catch (_err) {
          // If subscription add fails, still allow checkout (cart may be unchanged)
        }

        window.location.href = '/checkout';
      },
      { once: true }
    );
  });
}

initAuthDialog();
initConfirmDialog();
initCartSubscribeUI();

// If returning from login/register, re-open the cart drawer
try {
  const url = new URL(window.location.href);
  if (url.searchParams.get('subscribe') === '1') {
    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    drawer?.open?.();
    setTimeout(() => {
      document.querySelector('[data-subscribe-checkout]')?.scrollIntoView({ block: 'nearest' });
    }, 300);
  }
} catch (_e) {
  // no-op
}

